import React, { useEffect, useState, useCallback } from 'react';
import api from '../api/axios';
import { useToast } from '../context/ToastContext';
import { useAuth } from '../context/AuthContext';

// ─── helpers ──────────────────────────────────────────────────────────────────
const fmt = (n) => `₹${parseFloat(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
const today = () => new Date().toISOString().split('T')[0];

// ─── CounterSalesPage ──────────────────────────────────────────────────────────
const CounterSalesPage = () => {
  const toast = useToast();
  const { user } = useAuth();

  // Data
  const [products, setProducts]       = useState([]);
  const [apartments, setApartments]   = useState([]);
  const [sales, setSales]             = useState([]);
  const [todaySales, setTodaySales]   = useState(0);
  const [todayRevenue, setTodayRevenue] = useState(0);
  const [totalSalesCount, setTotalSalesCount] = useState(0);

  // UI state
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [filterDate, setFilterDate] = useState(today());
  const [filterApt, setFilterApt]   = useState('');
  const [showForm, setShowForm]     = useState(false);

  // Cart / new sale form
  const [form, setForm] = useState({ apartmentId: '', staffName: user?.fullName || user?.username || '', customerName: '', customerPhone: '', notes: '' });
  const [cart, setCart] = useState([]); // [{ productId, name, price, quantity, stock }]
  const [productSearch, setProductSearch] = useState('');

  // ── Loaders ─────────────────────────────────────────────────────────────────
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterDate) params.date = filterDate;
      if (filterApt)  params.apartmentId = filterApt;

      const [salesRes, productsRes, apts] = await Promise.all([
        api.get('/counter-sales', { params }),
        api.get('/products?limit=200'),
        api.get('/apartments'),
      ]);

      setSales(salesRes.data.data.sales);
      setTodaySales(salesRes.data.data.todaySales);
      setTodayRevenue(salesRes.data.data.todayRevenue);
      setTotalSalesCount(salesRes.data.data.total);
      setProducts(productsRes.data.data.products);
      setApartments(apts.data.data);
    } catch {
      toast('Failed to load data', 'error');
    } finally {
      setLoading(false);
    }
  }, [filterDate, filterApt, toast]);

  useEffect(() => { loadData(); }, [loadData]);

  // ── Cart helpers ────────────────────────────────────────────────────────────
  const addToCart = (product) => {
    setCart(prev => {
      const existing = prev.find(i => i.productId === product.id);
      if (existing) {
        if (existing.quantity >= product.stock_quantity) {
          toast(`Only ${product.stock_quantity} in stock`, 'error');
          return prev;
        }
        return prev.map(i => i.productId === product.id ? { ...i, quantity: i.quantity + 1 } : i);
      }
      if (product.stock_quantity <= 0) { toast('Out of stock', 'error'); return prev; }
      return [...prev, {
        productId: product.id,
        name: product.name,
        category: product.category,
        price: parseFloat(product.price),
        quantity: 1,
        stock: product.stock_quantity,
      }];
    });
  };

  const removeFromCart = (productId) => setCart(prev => prev.filter(i => i.productId !== productId));

  const updateCartQty = (productId, qty) => {
    const num = parseInt(qty);
    if (isNaN(num) || num < 1) return;
    setCart(prev => prev.map(i => {
      if (i.productId !== productId) return i;
      if (num > i.stock) { toast(`Only ${i.stock} in stock`, 'error'); return i; }
      return { ...i, quantity: num };
    }));
  };

  const cartTotal = cart.reduce((s, i) => s + i.price * i.quantity, 0);

  const resetForm = () => {
    setCart([]);
    setForm({ apartmentId: '', staffName: user?.fullName || user?.username || '', customerName: '', customerPhone: '', notes: '' });
    setProductSearch('');
  };

  // ── Submit ──────────────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (cart.length === 0) { toast('Add at least one product', 'error'); return; }
    setSaving(true);
    try {
      await api.post('/counter-sales', {
        apartmentId: form.apartmentId || undefined,
        staffName: form.staffName || undefined,
        customerName: form.customerName || undefined,
        customerPhone: form.customerPhone || undefined,
        notes: form.notes || undefined,
        items: cart.map(i => ({ productId: i.productId, quantity: i.quantity })),
      });
      toast('Counter sale recorded successfully!', 'success');
      resetForm();
      setShowForm(false);
      loadData();
    } catch (err) {
      toast(err.response?.data?.message || 'Failed to record sale', 'error');
    } finally {
      setSaving(false);
    }
  };

// ── Delete ──────────────────────────────────────────────────────────────────
  const handleDelete = async (id) => {
    if (!window.confirm('Delete this sale? Stock will be restored.')) return;
    try {
      await api.delete(`/counter-sales/${id}`);
      toast('Sale deleted and stock restored', 'success');
      loadData();
    } catch {
      toast('Failed to delete sale', 'error');
    }
  };

  const viewInvoice = (saleId) => {
    window.open(`${api.defaults.baseURL}/counter-sales/${saleId}/invoice`, '_blank');
  };

  const filteredProducts = products.filter(p =>
    !productSearch || p.name.toLowerCase().includes(productSearch.toLowerCase()) ||
    p.category.toLowerCase().includes(productSearch.toLowerCase())
  );

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div>
      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h2 style={{ fontSize: 22, fontWeight: 700 }}>🏪 Counter / Walk-in Sales</h2>
          <p style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 4 }}>
            Record live counter sales — stock deducted automatically, revenue counted in reports.
          </p>
        </div>
        <button className="btn btn-primary" onClick={() => { setShowForm(true); resetForm(); }}>
          + New Counter Sale
        </button>
      </div>

      {/* ── Stats ───────────────────────────────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 16, marginBottom: 24 }}>
        {[
          { label: "Today's Sales", value: todaySales, icon: '🧾', color: 'var(--primary)' },
          { label: "Today's Revenue", value: fmt(todayRevenue), icon: '💰', color: 'var(--success)' },
          { label: 'Cart Total', value: fmt(cartTotal), icon: '🛒', color: '#8b5cf6' },
        ].map(s => (
          <div key={s.label} className="dash-card" style={{ padding: '16px 20px' }}>
            <div style={{ fontSize: 24 }}>{s.icon}</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: s.color, marginTop: 4 }}>{s.value}</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* ── New Sale Form (Modal) ────────────────────────────────────────────── */}
      {showForm && (
        <div className="modal-overlay" onClick={() => setShowForm(false)}>
          <div className="modal" style={{ maxWidth: 900, width: '95vw', maxHeight: '92vh', overflow: 'auto' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">🏪 New Counter Sale</h3>
              <button className="modal-close" onClick={() => setShowForm(false)}>✕</button>
            </div>

            <form onSubmit={handleSubmit}>
              {/* Sale Details */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 20 }}>
                <div className="form-group">
                  <label className="form-label">Apartment (optional)</label>
                  <select className="form-select" value={form.apartmentId} onChange={e => setForm(p => ({ ...p, apartmentId: e.target.value }))}>
                    <option value="">Walk-in (no apartment)</option>
                    {apartments.map(a => <option key={a.id} value={a.id}>{a.apartment_name}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Customer Name (optional)</label>
                  <input className="form-input" placeholder="Name" value={form.customerName} onChange={e => setForm(p => ({ ...p, customerName: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Customer Mobile (optional)</label>
                  <div style={{ display: 'flex', alignItems: 'center', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' }}>
                    <div style={{ padding: '0 14px', fontWeight: 600, color: 'var(--text-3)', background: '#f8fafc', borderRight: '1px solid var(--border)', height: '100%', display: 'flex', alignItems: 'center', whiteSpace: 'nowrap' }}>+91</div>
                    <input className="form-input" type="tel" placeholder="9876543210" style={{ border: 'none', borderRadius: 0 }} value={form.customerPhone.replace('+91 ', '')} onChange={e => { const v = e.target.value.replace(/\D/g, '').slice(0, 10); setForm(p => ({ ...p, customerPhone: v ? `+91 ${v}` : '' })); }} maxLength={10} pattern="[0-9]{10}" />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Staff Name (optional)</label>
                  <input className="form-input" placeholder="Who is serving?" value={form.staffName} onChange={e => setForm(p => ({ ...p, staffName: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Notes (optional)</label>
                  <input className="form-input" placeholder="Any notes..." value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} />
                </div>
              </div>

              {/* Two-panel: Product picker + Cart */}
              <div className="resp-grid-2">
                {/* Left: Product search & pick */}
                <div>
                  <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 10 }}>📦 Select Products</div>
                  <input
                    className="form-input"
                    placeholder="Search products..."
                    value={productSearch}
                    onChange={e => setProductSearch(e.target.value)}
                    style={{ marginBottom: 10 }}
                  />
                  <div style={{ maxHeight: 320, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {filteredProducts.length === 0 && <div style={{ color: 'var(--text-muted)', textAlign: 'center', padding: 20 }}>No products found</div>}
                    {filteredProducts.map(p => {
                      const inCart = cart.find(i => i.productId === p.id);
                      const outOfStock = p.stock_quantity <= 0;
                      return (
                        <div
                          key={p.id}
                          onClick={() => !outOfStock && addToCart(p)}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
                            borderRadius: 10, border: `1px solid ${inCart ? 'var(--primary)' : 'var(--border)'}`,
                            background: inCart ? 'rgba(22,163,74,0.08)' : 'var(--bg-card)',
                            cursor: outOfStock ? 'not-allowed' : 'pointer',
                            opacity: outOfStock ? 0.5 : 1,
                            transition: 'all 0.15s',
                          }}
                        >
                          {p.image_url
                            ? <img src={p.image_url} alt={p.name} style={{ width: 36, height: 36, borderRadius: 8, objectFit: 'cover' }} />
                            : <div style={{ width: 36, height: 36, borderRadius: 8, background: 'var(--dark-3)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>🛒</div>
                          }
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 600, fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name}</div>
                            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{p.category} · Stock: {p.stock_quantity}</div>
                          </div>
                          <div style={{ fontWeight: 700, color: 'var(--primary)', whiteSpace: 'nowrap' }}>₹{p.price}</div>
                          {inCart && <div style={{ color: 'var(--success)', fontSize: 16 }}>✓</div>}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Right: Cart */}
                <div>
                  <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 10 }}>🛒 Cart ({cart.length} items)</div>
                  {cart.length === 0 ? (
                    <div style={{ background: 'var(--bg-card)', borderRadius: 12, padding: 30, textAlign: 'center', color: 'var(--text-muted)', border: '1px dashed var(--border)' }}>
                      <div style={{ fontSize: 32 }}>🛒</div>
                      <p>Click products to add them</p>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 320, overflowY: 'auto' }}>
                      {cart.map(item => (
                        <div key={item.productId} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 10, border: '1px solid var(--border)', background: 'var(--bg-card)' }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 600, fontSize: 13 }}>{item.name}</div>
                            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>₹{item.price} each</div>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <button type="button" style={{  background: 'var(--dark-3)', border: 'none', borderRadius: 6, width: 28, height: 28, cursor: 'pointer', color: 'var(--text)', fontSize: 16 }} onClick={() => updateCartQty(item.productId, item.quantity - 1)}>−</button>
                            <input type="number" value={item.quantity} min={1} max={item.stock} onChange={e => updateCartQty(item.productId, e.target.value)} style={{ width: 44, textAlign: 'center', background: 'var(--dark-3)', border: '1px solid var(--border)', borderRadius: 6, padding: '4px 6px', color: 'var(--text)', fontSize: 13 }} />
                            <button type="button" style={{ background: 'var(--dark-3)', border: 'none', borderRadius: 6, width: 28, height: 28, cursor: 'pointer', color: 'var(--text)', fontSize: 16 }} onClick={() => updateCartQty(item.productId, item.quantity + 1)}>+</button>
                          </div>
                          <div style={{ fontWeight: 700, minWidth: 70, textAlign: 'right', color: 'var(--primary)' }}>₹{(item.price * item.quantity).toFixed(2)}</div>
                          <button type="button" onClick={() => removeFromCart(item.productId)} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 18 }}>✕</button>
                        </div>
                      ))}
                    </div>
                  )}
                  {cart.length > 0 && (
                    <div style={{ marginTop: 14, padding: '14px 16px', background: 'var(--bg-card)', borderRadius: 10, border: '1px solid var(--border)' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 800, fontSize: 17 }}>
                        <span>Total</span>
                        <span style={{ color: 'var(--success)' }}>{fmt(cartTotal)}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="modal-footer" style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid var(--border)', justifyContent: 'flex-end', display: 'flex', gap: 10 }}>
                <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving || cart.length === 0}>
                  {saving ? 'Saving...' : `✅ Record Sale — ${fmt(cartTotal)}`}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Filters ─────────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' }}>
        <div className="form-group" style={{ margin: 0 }}>
          <label className="form-label" style={{ fontSize: 12 }}>Filter by Date</label>
          <input type="date" className="form-input" value={filterDate} onChange={e => setFilterDate(e.target.value)} style={{ width: 180 }} />
        </div>
        <div className="form-group" style={{ margin: 0 }}>
          <label className="form-label" style={{ fontSize: 12 }}>Filter by Apartment</label>
          <select className="form-select" value={filterApt} onChange={e => setFilterApt(e.target.value)} style={{ width: 200 }}>
            <option value="">All Apartments</option>
            {apartments.map(a => <option key={a.id} value={a.id}>{a.apartment_name}</option>)}
          </select>
        </div>
        <button className="btn btn-secondary btn-sm" style={{ alignSelf: 'flex-end' }} onClick={() => { setFilterDate(''); setFilterApt(''); }}>
          Clear Filters
        </button>
      </div>

      {/* ── Sales Table ─────────────────────────────────────────────────────── */}
      <div className="dash-card">
        <div className="dash-card-header">
          <div className="dash-card-title">Sales Records ({totalSalesCount})</div>
        </div>
        {loading ? (
          <div className="flex-center" style={{ height: 200 }}><div className="spinner"></div></div>
        ) : sales.length === 0 ? (
          <div className="empty-state" style={{ padding: '32px 0' }}>
            <div className="empty-icon">🏪</div>
            <p>No counter sales recorded {filterDate ? `for ${filterDate}` : ''}.</p>
            <button className="btn btn-primary" onClick={() => setShowForm(true)}>+ Record First Sale</button>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Apartment</th>
                  <th>Customer Info</th>
                  <th>Items</th>
                  <th>Total</th>
                  <th>Staff</th>
                  <th>Notes</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {sales.map(sale => (
                  <tr key={sale.id}>
                    <td style={{ fontFamily: 'monospace', fontSize: 13 }}>
                      {new Date(sale.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                        {new Date(sale.created_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                      </div>
                    </td>
                    <td>{sale.apartment_name || <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>Walk-in</span>}</td>
                    <td>
                      {sale.customer_name ? <div>{sale.customer_name}</div> : null}
                      {sale.customer_phone ? <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>{sale.customer_phone}</div> : null}
                      {!sale.customer_name && !sale.customer_phone && <span style={{ color: 'var(--text-muted)' }}>—</span>}
                    </td>
                    <td>
                      <div style={{ maxWidth: 220 }}>
                        {(sale.items || []).map(item => (
                          <div key={item.productId} style={{ fontSize: 12, color: 'var(--text-2)', marginBottom: 2 }}>
                            {item.name} × {item.quantity}
                            <span style={{ color: 'var(--text-muted)', marginLeft: 4 }}>= ₹{item.subtotal?.toFixed(2)}</span>
                          </div>
                        ))}
                      </div>
                    </td>
                    <td style={{ fontWeight: 700, color: 'var(--success)' }}>{fmt(sale.total_amount)}</td>
                    <td>{sale.staff_name || '—'}</td>
                    <td style={{ color: 'var(--text-muted)', fontSize: 13 }}>{sale.notes || '—'}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 6 }}>
                        {sale.invoice_url && (
                          <button 
                            className="btn btn-primary btn-sm btn-icon" 
                            title="View Invoice"
                            onClick={() => viewInvoice(sale.id)}
                            style={{ background: 'var(--success)', borderColor: 'var(--success)' }}
                          >
                            📄 PDF
                          </button>
                        )}
                        <button className="btn btn-danger btn-sm btn-icon" title="Delete & Restore Stock" onClick={() => handleDelete(sale.id)}>🗑️</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default CounterSalesPage;
